import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';

const { FiInfo } = FiIcons;

// Tooltip content for each question
const tooltipContent = {
  // Demographics
  'age': 'We ask for your age to match you with dogs that fit your life stage and energy level.',
  'gender': 'Some breeds may have different relationships with different genders.',
  'height': 'Your height helps us recommend dogs that are proportionate to your physical stature.',
  'build': 'Your build helps us match you with dogs whose size and strength are compatible with your physical capabilities.',
  
  // Living Situation
  'housing-type': 'Different breeds have different space requirements based on their size and energy levels.',
  'yard-space': 'Some breeds need more outdoor space for exercise and play.',
  'household-members': 'This helps us match you with breeds that are good with children, other adults, or prefer quieter homes.',
  'other-pets': 'Some breeds get along better with other animals than others.',
  
  // Lifestyle
  'activity-level': 'Dogs have varying energy levels that should match your activity level for a harmonious relationship.',
  'work-schedule': 'Your availability affects how much time you can spend with your dog for exercise, training, and companionship.',
  'travel-frequency': 'Some breeds handle being left with sitters or kennels better than others.',
  'outdoor-activities': 'Different breeds excel at different activities, from hiking to swimming to lounging.',
  
  // Personality
  'personality-type': 'Your personality traits help us match you with a dog whose temperament complements yours.',
  'stress-level': 'How you handle stress can affect your compatibility with certain breeds.',
  'patience-level': 'Training and raising a dog requires patience, with some breeds needing more than others.',
  'emotional-needs': 'Different breeds fulfill different emotional roles, from protective guardians to affectionate companions.',
  'childhood-experience': 'Your past experiences with animals can influence your comfort level with different breeds.',
  
  // Geographic
  'location': 'Some breeds are more readily available in certain regions.',
  'climate': 'Certain breeds are better adapted to specific climates due to their coat type and physical characteristics.',
  
  // Care Capabilities
  'grooming-commitment': 'Different breeds require varying levels of grooming, from daily brushing to occasional maintenance.',
  'exercise-commitment': 'Dogs have different exercise needs that must be met for their physical and mental wellbeing.',
  'training-experience': 'Some breeds are easier to train than others, which matters based on your experience level.',
  'budget': 'The cost of food, grooming, vet care, and other expenses varies by breed size and specific health needs.',
  'vet-access': 'Some breeds require specialized veterinary care or more frequent visits.',
  
  // Preferences
  'noise-tolerance': 'Some breeds are more vocal than others, which may affect your living situation.',
  'cleanliness-preference': 'Factors like shedding and drooling vary greatly between breeds.',
  'social-interaction': 'Some breeds attract more attention and interaction with strangers than others.'
};

const QuestionTooltip = ({ questionId }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const content = tooltipContent[questionId] || 'This helps us find your perfect dog match!';
  
  return (
    <div className="relative ml-3">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        className="text-[#F2674C] hover:text-[#e85a3e] transition-colors p-1 rounded-full"
        aria-label="Why we ask this question"
      >
        <SafeIcon icon={FiInfo} className="w-5 h-5" />
      </button>
      
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-64 p-4 bg-white rounded-xl shadow-lg border border-gray-200 text-left right-0 top-full mt-2"
          >
            <p className="text-sm text-[#282f3a] font-medium">
              {content}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuestionTooltip;